import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import IncidentReportForm from "./IncidentReportForm";
import { createIncidentReport } from "../../../store/slices/incidentReportsSlice";

const AddIncidentReport = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isLoading, error } = useSelector((state) => state.incidentReports);
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        patient_id: patientId, // initialize
        case_id: caseId,    // initialize
        reportCompletedBy: "",
        dateOfReport: "",
        incidentDateTime: "",
        placeOfIncident: "",
        natureOfIncident: "",
        otherNature: "",
        summary: "",
        actionTaken: "",
        otherWitnesses: "",
        debriefingDetails: "",
        directorDateTime: "",
        otherComments: "",
        followUpNecessary: "",
        followUpNecessaryDetails: "",
        director_name: "",
        actionReportedToTrustees: "",
        trusteesDate: "",
        clients: [{ name: "" }],
        staff: [{ name: "" }],
        signatures: [{ name: "" }],
        otherNature: "",
    });

    // Handle normal field changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle nested arrays of objects (clients, staff, signatures)
    const handleNestedChange = (field, index, value) => {
        const updated = [...formData[field]];
        updated[index].name = value;
        setFormData((prev) => ({ ...prev, [field]: updated }));
    };

    const addNestedField = (field) => {
        setFormData((prev) => ({
            ...prev,
            [field]: [...prev[field], { name: "" }],
        }));
    };

    const removeNestedField = (field, index) => {
        const updated = [...formData[field]];
        updated.splice(index, 1);
        setFormData((prev) => ({ ...prev, [field]: updated }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Create a copy of formData to modify
        const formDataToSend = { ...formData };

        // Apply conditional nulling
        // 1. If natureOfIncident is not "Other", set otherNature to null
        if (formDataToSend.natureOfIncident !== "Other") {
            formDataToSend.otherNature = null;
        }

        // 2. If followUpNecessary is "No", set followUpNecessaryDetails to null
        if (formDataToSend.followUpNecessary?.toLowerCase() === "no") {
            formDataToSend.followUpNecessaryDetails = null;
        }

        // 3. If actionReportedToTrustees is "No", set trusteesDate to null
        if (formDataToSend.actionReportedToTrustees?.toLowerCase() === "no") {
            formDataToSend.trusteesDate = null;
        }

        const requiredFields = [
            "reportCompletedBy",
            "dateOfReport",
            "incidentDateTime",
            "placeOfIncident",
            "natureOfIncident",
            "followUpNecessary",
        ];

        // Adjust validation based on conditional logic
        const validationRules = [];

        // Only require otherNature if natureOfIncident is "Other"
        if (formDataToSend.natureOfIncident === "Other") {
            validationRules.push({ field: "otherNature", message: "Please specify the nature of incident" });
        }

        // Only require followUpNecessaryDetails if followUpNecessary is "Yes"
        if (formDataToSend.followUpNecessary?.toLowerCase() === "yes") {
            validationRules.push({ field: "followUpNecessaryDetails", message: "Please provide follow-up details" });
        }

        // Only require trusteesDate if actionReportedToTrustees is "Yes"
        if (formDataToSend.actionReportedToTrustees?.toLowerCase() === "yes") {
            validationRules.push({ field: "trusteesDate", message: "Please provide trustees date" });
        }

        // Check basic required fields
        const missingRequired = requiredFields.filter((f) => !formDataToSend[f]);
        if (missingRequired.length > 0) {
            Swal.fire({
                title: "Missing Fields",
                text: `Please fill: ${missingRequired.join(", ")}`,
                icon: "warning",
            });
            return;
        }

        // Check conditional required fields
        const missingConditional = validationRules.filter((rule) => !formDataToSend[rule.field]);
        if (missingConditional.length > 0) {
            const messages = missingConditional.map(rule => rule.message);
            Swal.fire({
                title: "Missing Fields",
                text: messages.join("\n"),
                icon: "warning",
            });
            return;
        }

        if (
            formDataToSend.clients.some((c) => !c.name) ||
            formDataToSend.staff.some((s) => !s.name) ||
            formDataToSend.signatures.some((s) => !s.name)
        ) {
            Swal.fire({
                title: "Validation Error",
                text: "Please fill all names in Clients, Staff, and Signatures.",
                icon: "warning",
            });
            return;
        }

        try {
            const result = await dispatch(createIncidentReport(formDataToSend)).unwrap();

            Swal.fire({
                title: "Success!",
                text: "Incident report added successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            const res = result.payload || result;
            navigate(`/phase05/incident-report/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (err) {
            console.error("Error creating incident report:", err);

            if (err && err.errors) {
                const errorMessages = Object.values(err.errors)
                    .flat()
                    .join("\n");
                Swal.fire({
                    title: "Validation Error",
                    text: errorMessages,
                    icon: "error",
                });
            } else {
                Swal.fire({
                    title: "Error",
                    text: "Failed to create incident report.",
                    icon: "error",
                });
            }
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="card">
                    <div className="card-body">
                        {error && (
                            <div className="alert alert-danger">
                                {typeof error === "object"
                                    ? Object.values(error).map((e, i) => <div key={i}>{e}</div>)
                                    : error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <IncidentReportForm
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={handleChange}
                                handleNestedChange={handleNestedChange}
                                addNestedField={addNestedField}
                                removeNestedField={removeNestedField}
                                isLoading={isLoading}
                            />
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4 sticky-form-submit"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <i className="ri-loader-4-line me-1 animate-spin"></i> Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-2-line me-1"></i> Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddIncidentReport;
